import { PageLoading } from '@/components/Loader';
import type { Permissions } from '@/domain/permission';
import { useAccessToken } from '@/hooks/authority/useAccessToken';
import { useIsLoggedIn } from '@/hooks/authority/useIsLoggedIn';
import { useMe } from '@/hooks/services/users';
import { usePermissions } from '@/hooks/usePermissions';
import ErrorBoundary from '@/pages/ErrorBoundary';
import { type PropifyRoute, routes, unauthorizedRoute } from '@/routes';
import { SentryService } from '@/services/sentry';
import { getPermissionsFromUser, hasPermissions, logout, setUserPermissions } from '@/utils/auth';
import { type FC, Suspense, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, type RouteObject, RouterProvider } from 'react-router';

const MAXIMUM_ME_RETRIES = 1;

const filterRoutes = (
  routesList: readonly PropifyRoute[],
  permissions: Permissions[],
): PropifyRoute[] =>
  routesList
    .map((route) =>
      !route.permissions || hasPermissions(route.permissions, permissions)
        ? route
        : { ...unauthorizedRoute, path: route.path, routes: route.routes },
    )
    .map((route) => ({
      ...route,
      routes: route.routes?.length ? filterRoutes(route.routes, permissions) : undefined,
    }));

const generateRoutes: (routesList: PropifyRoute[]) => RouteObject[] = (routesList) =>
  routesList.map((route) => ({
    path: route.path,
    element: route.redirect ? (
      <Navigate to={route.redirect} />
    ) : route.component ? (
      <Suspense fallback={<PageLoading />}>
        <route.component {...route.props} />
      </Suspense>
    ) : (
      <Outlet />
    ),
    children: !route.redirect && route.routes ? generateRoutes(route.routes) : undefined,
    ErrorBoundary,
  }));

export const Router: FC = () => {
  const { isLoggedIn } = useIsLoggedIn();
  const { permissions } = usePermissions();
  const [, setMeRetryCount] = useState(0);
  const { accessToken } = useAccessToken();

  const {
    data: currentUser,
    error,
    refetch: refetchUser,
  } = useMe({
    disabled: !isLoggedIn,
  });

  useEffect(() => {
    if (!error) {
      return;
    }
    setMeRetryCount((meRetryCount) => {
      if (meRetryCount >= MAXIMUM_ME_RETRIES) {
        logout();
        return 0;
      }
      return meRetryCount + 1;
    });
  }, [error]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    setUserPermissions(getPermissionsFromUser(currentUser));
  }, [currentUser, accessToken]);

  useEffect(() => {
    if (!accessToken || !currentUser) {
      return;
    }
    refetchUser();
  }, [accessToken]);

  const permissionsRouter = useMemo(
    () =>
      SentryService.sentryCreateBrowserRouter([
        {
          path: '/',
          element: <Outlet />,
          children: generateRoutes(filterRoutes(routes, isLoggedIn ? permissions : [])),
          ErrorBoundary,
        },
      ]),
    [isLoggedIn, permissions],
  );

  const key = currentUser ? currentUser.id + permissions.join(',') : 'loggedOut';

  return <RouterProvider key={key} router={permissionsRouter} />;
};
